<script lang="ts" setup>
	import StringMethods from '~/utils/character'
	import type { UiConfigType } from './card.vue'

	export type SummaryContext = Record<
		string,
		| string
		| number
		| {
				value: unknown
				key?: string | number
				callback?: Function
				pClass?: ClassContext
				hClass?: ClassContext
				wrapperClass?: ClassContext
				disabled?: boolean
		  }
	>

	withDefaults(
		defineProps<{
			summary: SummaryContext
			loading?: boolean
			bg?: string
			noDefaultClass?: boolean
			limitWidth?: boolean
			ui?: UiConfigType
		}>(),
		{
			limitWidth: true
		}
	)

	const { stripUnderScore } = StringMethods()
	const defaultUi = {
		container: '!shadow-none space-y-8 sm:max-w-[800px] max-auto md:px-0',
		wrapper: 'max-w-full'
	}
</script>

<template>
	<div class="mx-auto">
		<shared-card
			:loading="loading"
			:loader-height="500"
			:loader-width="564"
			:default-class="!noDefaultClass ? 'sm:border sm:p-5 md:p-14 w-full flex flex-col justify-center' : ''"
			:ui="{
				container: cn(defaultUi.container, ui?.container),
				wrapper: cn(defaultUi.wrapper, ui?.wrapper)
			}"
		>
			<div
				class="mx-auto w-full rounded-lg sm:p-3 md:p-5"
				:class="[bg || 'bg-purple-25', limitWidth ? 'sm:max-w-[564px]' : '']"
			>
				<div class="w-full rounded-lg border bg-white px-2 py-4 md:px-4">
					<template v-for="(val, k) in summary" :key="k">
						<div
							class="flex-wrap justify-between gap-3 border-b border-gray-100 px-3 py-4 font-medium capitalize first:pt-2 sm:px-0"
							:class="[
								(typeof val === 'object' && val?.wrapperClass) || 'flex',
								(typeof val === 'object' && val?.disabled) || !val ? 'hidden' : 'flex'
							]"
						>
							<slot
								:name="`body(${k})`"
								:k="stripUnderScore(k as string)"
								:value="val"
								key-class="text-gray-500 text-base"
								value-class="text-gray-600 font-semibold"
							>
								<span :class="(typeof val === 'object' && val.hClass) || 'text-base text-gray-500'">
									{{ stripUnderScore(k as string) }}
								</span>

								<div v-if="typeof val === 'object'" class="flex items-center gap-x-1">
									<span class="" :class="val?.pClass || 'font-semibold text-gray-600'">
										<slot :key="k" :name="`value(${val?.key})`" :content="val">
											{{ val.value }}
										</slot>
									</span>
									<span v-if="val?.callback">
										-
										<span
											class="cursor-pointer font-semibold text-primary-2 underline"
											role="button"
											aria-label="view"
											@click="() => val.callback?.()"
										>
											view
										</span>
									</span>
								</div>

								<span v-else class="font-semibold text-gray-600">
									{{ val }}
								</span>
							</slot>
						</div>
					</template>
				</div>
			</div>
			<slot />
		</shared-card>
	</div>
</template>
